/**
 * FullStacked Cloud Console - Shell billing state: GET /v1/billing on load and after any 402
 * (console.md §7), falling back to the access profile's data.billing; and the return from the
 * auth host's billing flow (`?billing_return=1`), which syncs the plan once (POST /v1/billing/sync).
 */

import { useCallback, useEffect, useState } from "react";
import type { UserAccess } from "../../../src/common/types.ts";
import { billingFromAccess, getBilling, syncBilling, type BillingInfo } from "../api/billing.ts";
import type { ApiContext } from "../api/client.ts";
import { takeBillingReturn } from "../lib/billing.ts";

export function useBilling(
    api: ApiContext,
    access: UserAccess | null
): { billing: BillingInfo | null; reload: () => void } {
    const [fetched, setFetched] = useState<BillingInfo | null>(null);
    const [tick, setTick] = useState(0);
    const [returned] = useState(() => {
        const { returned, path } = takeBillingReturn(window.location.href);
        if (returned) window.history.replaceState(window.history.state, document.title, path);
        return returned;
    });

    useEffect(() => {
        let cancelled = false;
        const first = returned && tick === 0;
        // Back from Clerk Billing: refresh the plan from Clerk (429 within 30 s: just read it).
        const load = first ? syncBilling(api).catch(() => getBilling(api)) : getBilling(api);
        load.then(
            (b) => !cancelled && setFetched(b),
            () => undefined
        );
        return () => {
            cancelled = true;
        };
    }, [api, tick, returned]);

    const reload = useCallback(() => setTick((t) => t + 1), []);
    return { billing: fetched ?? billingFromAccess(access), reload };
}
